import PropTypes from "prop-types";

// MUI
import { Box, FormHelperText, List, Stack, SvgIcon } from "@mui/material";

// Components
import SimpleDoubleChecking from "@Layouts/components/Buttons/SimpleDoubleChecking";
import SimpleIconButton from "@Layouts/components/Buttons/SimpleIconButton";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Icons
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";

/****************************************** HELPERS ******************************************/
function displayErrorMessage(entry, showErrorMessage, errorMessage) {
	if (showErrorMessage && (!entry || (typeof entry === "object" && areAllKeysFalsyExcludingZero(entry)))) {
		return (
			<FormHelperText id="error-text" className={"!p-0 !mt-[-5px] !text-alert !font-sans !text-[13px]"}>
				{errorMessage ?? "Please select an option"}
			</FormHelperText>
		);
	}
	return null;
}

const areAllKeysFalsyExcludingZero = (obj) => {
	// Check if any value is 0 or truthy; if found, return false
	for (const value of Object.values(obj)) {
		if (value === 0 || value) {
			return false;
		}
	}
	// If none of the values are 0 or truthy, return true
	return true;
};

export default function MultipleSelectGroup(props) {
	const { selectBy = "label", orderBy = "label", showBy = "label", selectableOptions = [] } = props;

	const moreThanOneBlankSelected = (array) => array.filter((element) => element === "").length > 0;

	const getSelectableOptions = (entry) => {
		let options = [...selectableOptions];
		const isIncluded = isValueIncluded(entry, options);

		if (entry && !isIncluded) {
			if (typeof entry === "string") {
				options.push({
					label: entry,
				});
			} else {
				options.push(entry);
			}
		}

		options = options.sort((item1, item2) => (item1[orderBy] > item2[orderBy] ? 1 : -1));
		return options;
	};

	const isValueIncluded = (entry, options) => {
		if (typeof entry === "string") {
			return options.some((option) => option[selectBy] === entry);
		}
		return options.some((option) => option[selectBy] === entry[selectBy]);
	};

	const showAdd = (arrayLength, order) => {
		if (!selectableOptions.length) {
			return false;
		}

		return order + 1 >= arrayLength;
	};

	const showDelete = (entry, arrayLength, showDeleteIcon) => {
		if (showDeleteIcon) {
			return true;
		}

		if (entry === "" && arrayLength === 1) {
			return false;
		}

		return !(arrayLength === 1 && !props.allowDeleteFirst);
	};

	const mapRightEntry = (entry) => {
		if (!entry) {
			return null;
		}
		if (typeof entry === "string") {
			const rightEntry = {};
			rightEntry[selectBy] = entry;
			return rightEntry;
		}
		return entry;
	};

	return (
		<List component={"ul"} disablePadding id={"multiple-select-group"}>
			{props.items.length
				? props.items?.map((entry, order) => {
						return (
							<Stack key={entry ?? order} direction="row" component={"li"} display={"inline-flex"} alignItems={"center"}>
								<Stack direction="column">
									<Stack direction="row" alignItems={"center"}>
										<SmartSelector
											showBy={showBy}
											selectBy={selectBy}
											addable={props.allowNewValue}
											disabledOptions={props.disabledOptions}
											options={getSelectableOptions(entry) || []}
											selected={mapRightEntry(entry)}
											inputProps={{
												placeholder: props.placeholder,
												hideTooltip: props.hideTooltip,
												sx: props.inputProps,
											}}
											clearable={!props.disableClearable}
											disabled={props.disabled}
											error={props?.errorsList ? props?.errorsList[order] : false}
											loading={props.loading}
											classes={props.className}
											sx={props?.sxSelect || { minWidth: "216px" }}
											onChangeAction={(selected) => props.onChange(selected, order)}
										/>
										{showDelete(entry, props.items.length, props.showDeleteIcon) && (
											<SimpleDoubleChecking
												sx={props.sxButton}
												confirmationSx={props.confirmationSx}
												disabled={props.disabled || props.deleting}
												color={"var(--gray-blue-color)"}
												confirmationButtonColor={"var(--gray-blue-color)"}
												icon={<CloseRoundedIcon fontSize="small" />}
												iconHover={<CloseRoundedIcon fontSize="small" />}
												confirmationIcon={<SvgIcon className="!text-[25px]" component={TrashIcon} />}
												size={props.size}
												onDeleteAction={() => props.onDelete(order)}
											/>
										)}
										{showAdd(props.items.length, order) && (
											<>
												<SimpleIconButton
													sx={props.sxButton || props.sxAddButton}
													className={"btn-add !ml-[4px]"}
													color={"var(--primary-color)"}
													disabled={
														props.disabled || props.disableAddButton || moreThanOneBlankSelected(props.items)
													}
													icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
													iconHover={<AddCircleRounded fontSize="medium" />}
													onClick={() => props.onAdd()}
												/>
											</>
										)}
									</Stack>
									{displayErrorMessage(entry, props.showErrorMessage, props.errorMessage)}
								</Stack>
							</Stack>
						);
				  })
				: props.allowDeleteFirst && (
						<Box>
							<SimpleIconButton
								sx={props.sxButton}
								className={`!ml-[-10px] ${props.disableAddButton ? "!text-[#cccccc]" : ""}`}
								color={"var(--primary-color)"}
								disabled={props.disableAddButton || moreThanOneBlankSelected(props.items)}
								icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
								iconHover={<AddCircleRounded fontSize="medium" />}
								onClick={() => {
									props.onAdd();
								}}
							/>
						</Box>
				  )}
		</List>
	);
}

MultipleSelectGroup.propTypes = {
	allowDeleteFirst: PropTypes.bool,
	identifier: PropTypes.string,
	items: PropTypes.array,
	selectableOptions: PropTypes.array,
	placeholder: PropTypes.string,
	deleting: PropTypes.bool,
	onChange: PropTypes.func,
	onDelete: PropTypes.func,
	onAdd: PropTypes.func,
	disabled: PropTypes.bool,
	loading: PropTypes.bool,
	errorsList: PropTypes.array,
	disableAddButton: PropTypes.bool,
	disabledOptions: PropTypes.array,
	disableClearable: PropTypes.bool,
	sxButton: PropTypes.object,
	showDeleteIcon: PropTypes.bool,
	sxAddButton: PropTypes.object,
	sxSelect: PropTypes.object,
	showBy: PropTypes.string,
	selectBy: PropTypes.string,
	orderBy: PropTypes.string,
	errorMessage: PropTypes.string,
	className: PropTypes.string,
	showErrorMessage: PropTypes.bool,
	allowNewValue: PropTypes.bool,
	size: PropTypes.string,
	confirmationSx: PropTypes.object,
	hideTooltip: PropTypes.bool,
	inputProps: PropTypes.object,
};
